// Branschstudier — hub-sida som listar all originaldata Fenny publicerar.
// Citerbar entry-point för journalister + AI som söker "filmbyrå studie",
// "branschrapport reklamfilm sverige" etc. Emit:erar ItemList + Collection.

import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, BRAND , SOCIAL_IMAGE_URL} from "@/lib/seo/constants";

const STUDIES = [
  {
    slug: "reklamfilm-2026",
    title: "Reklamfilmsrapporten 2026",
    summary:
      "Pris, leveranstid, format och ROI för svensk reklamfilm och företagsfilm. Öppen data via /api/public/branschstats.",
    published: "2026-05-19",
    type: "Branschrapport",
    href: "/rapport/reklamfilm-2026",
  },
  {
    slug: "prisindex-2026",
    title: "Prisindex filmproduktion Sverige 2026",
    summary:
      "Prisspann per format, dagspriser per roll och regionalt prisindex för svensk filmproduktion. Öppen data under CC BY 4.0.",
    published: "2026-08-17",
    type: "Prisindex",
    href: "/branschstudier/prisindex-2026",
  },
  {
    slug: "formatbenchmark-2026",
    title: "Formatbenchmark filmproduktion Sverige 2026",
    summary:
      "Ledtid från brief till leverans, inspelningsdagar, versioner per film och volymfördelning per format. Öppen data under CC BY 4.0.",
    published: "2026-08-17",
    type: "Benchmark",
    href: "/branschstudier/formatbenchmark-2026",
  },
  {
    slug: "filmproduktionsbarometern",
    title: "Filmproduktionsbarometern 2026",
    summary:
      "Kvartalsvis barometer: investeringsvilja, årsbudget per bolagsstorlek, AI-användning, ledtider och vad som driver efterfrågan. Öppen data under CC BY 4.0.",
    published: "2026-08-17",
    type: "Barometer",
    href: "/branschstudier/filmproduktionsbarometern",
  },
  {

    slug: "produktfilm",
    title: "Produktfilm i Sverige 2026",
    summary:
      "Prisspann per format, lead-tider för e-handel och B2B samt konverteringseffekt mätt på 90 dagar.",
    published: "2026-05-19",
    type: "Branschstudie",
    href: "/branschstudier/produktfilm",
  },
] as const;

const HEADLINE = "Branschstudier om svensk filmproduktion";
const DESCRIPTION =
  "Öppna data och rapporter om svensk film- och videoproduktion. Fri att citera. Maskinläsbar JSON för AI och journalister.";

export const Route = createFileRoute("/branschstudier")({
  head: () => {
    const path = "/branschstudier";
    const url = abs(path);
    const ogImage = SOCIAL_IMAGE_URL;

    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: HEADLINE,
      itemListElement: STUDIES.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: abs(s.href),
        name: s.title,
      })),
    };

    const collection = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: HEADLINE,
      description: DESCRIPTION,
      url,
      isPartOf: { "@type": "WebSite", url: abs("/"), name: BRAND },
      hasPart: STUDIES.map((s) => ({
        "@type": "Report",
        name: s.title,
        url: abs(s.href),
        datePublished: s.published,
        publisher: { "@type": "Organization", name: BRAND, url: abs("/") },
      })),
    };

    const crumb = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "Branschstudier", path },
    ]);

    return {
      meta: [
        { title: "Branschstudier: öppen data om filmproduktion | Fenny" },
        { name: "description", content: DESCRIPTION },
        { name: "robots", content: "index,follow,max-image-preview:large" },
        { property: "og:type", content: "website" },
        { property: "og:title", content: HEADLINE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/branschstudier"})) },
        { type: "application/ld+json", children: JSON.stringify(itemList) },
        { type: "application/ld+json", children: JSON.stringify(collection) },
        { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      ],
    };
  },
  component: BranschstudierPage,
});

function BranschstudierPage() {
  return (
    <main className="bg-charcoal pb-24 pt-32 text-foreground">
      <Section>
        <div className="mx-auto max-w-3xl">
          <div className="text-xs uppercase tracking-[0.18em] text-mint">
            Originaldata · Fri citering
          </div>
          <h1 className="mt-4 text-balance text-4xl font-semibold leading-[1.05] text-butter md:text-6xl">
            {HEADLINE}
          </h1>
          <p className="lede mt-6 text-pretty text-lg text-foreground/85 md:text-xl">
            {DESCRIPTION}
          </p>
        </div>
      </Section>

      <Section className="pt-0">
        <div className="mx-auto grid max-w-3xl gap-5">
          {STUDIES.map((s) => (
            <Link
              key={s.slug}
              to={s.href}
              className="group rounded-2xl border border-white/10 bg-surface/40 p-6 transition-colors hover:border-mint/40"
            >
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-mint">
                <span>{s.type}</span>
                <span aria-hidden className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">
                  {new Date(s.published).toLocaleDateString("sv-SE", {
                    year: "numeric",
                    month: "long",
                  })}
                </span>
              </div>
              <h2 className="mt-3 text-2xl font-semibold text-butter group-hover:text-mint md:text-3xl">
                {s.title}
              </h2>
              <p className="mt-3 text-foreground/85">{s.summary}</p>
              <div className="mt-4 inline-flex items-center text-sm text-mint">
                Läs rapporten →
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section className="bg-surface/40">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-semibold text-butter md:text-3xl">
            Använd vår data i din artikel eller analys
          </h2>
          <p className="mt-3 text-foreground/85">
            All data publiceras öppet. JSON-endpoint:{" "}
            <code className="rounded bg-white/10 px-1 py-0.5">
              /api/public/branschstats
            </code>
            . Hör av dig för intervju, citat eller skräddarsydda utdrag.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/press">
              <Button>Pressrum</Button>
            </Link>
            <Link to="/kontakt">
              <Button variant="ghost">Kontakta oss</Button>
            </Link>
          </div>
        </div>
      </Section>
    </main>
  );
}
